import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from '../../core';
import useScrollToTop from '../../hooks/useScrollTop';
import { ReactComponent as XIcon } from '../../images/icons/collapse-arrow.svg';
import Header from '../Header';
import { usePageActions } from '../PageActions/context';
import SidebarStyled from '../Sidebar';

import MenuMobile from './components/MenuMobile';

type LayoutWithSidebarProps = {
  className?: string;
};

const LayoutWithSidebar = ({ className = '' }: LayoutWithSidebarProps) => {
  const { isFullScreen } = usePageActions();
  const [isMenuClosed, setIsMenuClosed] = useState<boolean>(false);

  useScrollToTop();

  return (
    <div className={className}>
      {!isFullScreen && <Header className="header desktop-menu" />}
      <MenuMobile className="header mobile-menu" />
      {!isFullScreen && (
        <SidebarStyled
          isMenuClosed={isMenuClosed}
          setIsMenuClosed={() => true}
          className={`sidebar ${isMenuClosed ? 'close-sidebar' : 'open-sidebar'}`}
        >
          {!isMenuClosed ? (
            <div className="start-position">
              <Button
                onClick={() => setIsMenuClosed(true)}
                variant="secondary"
                size="XS"
                iconOnly
                icon={<XIcon />}
                aria-label="Collapse the sidebar"
                className={`open-btn ${!isMenuClosed ? 'rotated' : ''}`}
              />
            </div>
          ) : (
            <div className="end-position">
              <Button
                onClick={() => setIsMenuClosed(false)}
                variant="secondary"
                size="XS"
                iconOnly
                icon={<XIcon />}
                aria-label="Expand the sidebar"
                className={`close-btn ${isMenuClosed ? '' : 'rotated'}`}
              />
            </div>
          )}
        </SidebarStyled>
      )}{' '}
      <div className={`content ${isFullScreen ? 'content--full-screen' : ''}`}>
        <main className={`content__main ${isFullScreen ? 'content__main--full-screen' : ''}`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

const StyledLayoutWithSidebar = styled(LayoutWithSidebar)`
  display: grid;
  grid-gap: 0;
  grid-template-columns: auto 1fr;
  grid-template-areas:
    'header  header'
    'sidebar content'
    'sidebar footer';
  overflow: hidden;

  > .header {
    grid-area: header;

    &.mobile-menu {
      display: block;
      @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
        display: none;
      }
    }

    &.desktop-menu {
      display: none;
      @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
        display: flex;
      }
    }
  }

  > .sidebar {
    display: none;

    @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      overflow-x: hidden;
      grid-area: sidebar;
      height: calc(100vh - 65px);
    }
  }

  > .open-sidebar {
    animation: slide-out 0.2s ease-in-out;
    animation-fill-mode: forwards;

    @keyframes slide-out {
      from {
        width: 72px;
      }

      to {
        width: 272px;
      }
    }
  }

  > .close-sidebar {
    animation: slide-in 0.2s ease-in-out;
    animation-fill-mode: forwards;

    @keyframes slide-in {
      from {
        width: 272px;
      }

      to {
        width: 72px;
      }
    }
  }

  /* Without the animation the sidebar would fall back to width: auto, so each block sets the final
     width of its keyframe: the same layout, reached without the slide. */
  @media (prefers-reduced-motion: reduce) {
    > .open-sidebar {
      animation: none;
      width: 272px;
    }

    > .close-sidebar {
      animation: none;
      width: 72px;
    }
  }

  > .content {
    width: 100%;
    grid-area: content;
    overflow-x: hidden;
    height: calc(100vh - 48px);

    @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
      height: calc(100vh - 65px);
    }

    &--full-screen {
      height: 100vh;
    }

    > .content__main {
      min-height: calc(100% - 45px);
      display: flex;
      justify-content: center;
      margin: 0 auto;

      /* Figma: a 1200px Page Container with 20px margins inside the 1240px page scroll, in every
         frame. The 20px margin takes effect from the breakpoint where the sidebar appears. */
      padding: 16px 16px 40px 16px;
      max-width: 1240px;

      @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
        padding-left: 20px;
        padding-right: 20px;
      }

      @media (min-width: ${({ theme }) => theme.breakpoints['2xl']}) {
        padding: 40px 20px 0 20px;
      }

      &--full-screen {
        padding: 16px;
      }
    }

    > .footer {
      grid-area: footer;
    }
  }

  .start-position {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .end-position {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  /* The box, size, border and states come from the core Button; the arrow is drawn on the 16px icon
     grid, centred, in currentColor, so only the grey glyph and its turn are set here. */
  .close-btn,
  .open-btn {
    color: ${({ theme }) => theme.neutralTextWeak};

    svg {
      transition: transform 0.2s ease;
    }

    &.rotated svg {
      transform: rotate(180deg);
    }
  }
`;

export default StyledLayoutWithSidebar;
